import * as fs from "node:fs"
import path from "node:path"
import * as ts from "typescript"

import { Parser } from "./parser.js"
import { ComponentDoc, ParserOptions } from "./types.js"
import { filterDuplicates } from "./utils/filter-duplicates.js"

type ComponentExportType = "explicit-export" | "inferred-export" | "star-export"

const sourceModuleFileExtensions = [".ts", ".tsx", ".mts", ".cts", ".js", ".jsx", ".mjs", ".cjs"]
const declarationModuleFileExtensions = [".d.ts", ".d.mts", ".d.cts"]

function isDeclarationFile(filePath: string): boolean {
  return declarationModuleFileExtensions.some(extension => filePath.endsWith(extension))
}

function moduleFileExtensionsFor(basePath: string): string[] {
  return isDeclarationFile(basePath)
    ? [...declarationModuleFileExtensions, ...sourceModuleFileExtensions]
    : [...sourceModuleFileExtensions, ...declarationModuleFileExtensions]
}

function findSiblingModuleFile(basePath: string, resolvedPath: string): string | null {
  const dir = path.dirname(resolvedPath)
  const basename = path.basename(resolvedPath)
  if (!fs.existsSync(dir)) {
    return null
  }

  for (const extension of moduleFileExtensionsFor(basePath)) {
    const candidate = path.join(dir, `${basename}${extension}`)
    if (fs.existsSync(candidate)) {
      return candidate
    }
  }

  return null
}

/**
 * resolve module path from the import whenever it is file or submodule
 */
function resolveModulePath(basePath: string, modulePath: string) {
  const resolvedPath = path.resolve(path.dirname(basePath), modulePath)

  if (fs.existsSync(resolvedPath)) {
    const stats = fs.statSync(resolvedPath)
    if (stats.isDirectory()) {
      // Find index file in directory
      for (const extension of moduleFileExtensionsFor(basePath)) {
        const indexFile = path.join(resolvedPath, `index${extension}`)
        if (fs.existsSync(indexFile)) {
          return indexFile
        }
      }

      return null
    }
  }

  // Find file with matching name but different extension
  const matchingFile = findSiblingModuleFile(basePath, resolvedPath)
  if (matchingFile) return matchingFile

  return null
}

export class ModuleParser {
  private checker: ts.TypeChecker
  private entryFilePath: string
  private parser: Parser
  private program: ts.Program

  constructor(program: ts.Program, entryFilePath: string, parserOptions: ParserOptions = {}) {
    this.program = program
    this.parser = new Parser(program, parserOptions)
    this.entryFilePath = entryFilePath
    this.checker = program.getTypeChecker()
  }

  public parseModule(): ComponentDoc[] {
    const sourceFile = this.program.getSourceFile(this.entryFilePath)

    if (!sourceFile) {
      throw new Error(`Could not find source file for path ${this.entryFilePath}`)
    }

    const componentsInfo = this.parseFile(sourceFile)
    return filterDuplicates(componentsInfo)
  }

  private applyExportType(componentDocs: ComponentDoc[], exportType: ComponentExportType): ComponentDoc[] {
    return componentDocs.map(componentDoc => ({ ...componentDoc, exportType }))
  }

  private parseFile(scannedSource: ts.SourceFile, exportType: ComponentExportType = "explicit-export"): ComponentDoc[] {
    const componentsInfo: ComponentDoc[] = []

    const moduleSymbol = this.checker.getSymbolAtLocation(scannedSource)
    if (!moduleSymbol) {
      throw new Error(`Could not find module symbol for source file: ${scannedSource.fileName}`)
    }

    // iterate through file symbols. We cannot use just getModuleExports. It don't show all exports. This is more reliable.
    ts.forEachChild(scannedSource, node => {
      // Handle named exports (export { X })
      if (ts.isExportDeclaration(node) && node.exportClause && ts.isNamedExports(node.exportClause)) {
        for (const exportSpecifier of node.exportClause.elements) {
          if (node.isTypeOnly || exportSpecifier.isTypeOnly) {
            continue
          }

          const symbol = this.checker.getSymbolAtLocation(exportSpecifier.name)
          if (symbol) {
            const parsedDocs = this.parser.parseExportSymbol(symbol, scannedSource)
            if (parsedDocs.length > 0) {
              componentsInfo.push(...this.applyExportType(parsedDocs, exportType))
            }
          }
        }

        return
      }

      // Handle direct exports of functions and classes (export class X, export const X, etc)
      if ((ts.isClassDeclaration(node) || ts.isFunctionDeclaration(node)) && hasExportModifier(node)) {
        const symbol = node.name ? this.checker.getSymbolAtLocation(node.name) : undefined
        if (symbol) {
          componentsInfo.push(...this.applyExportType(this.parser.parseExportSymbol(symbol, scannedSource), exportType))
        }

        return
      }

      // handle direct export of variables, e.g. export const X
      if (ts.isVariableStatement(node) && hasExportModifier(node)) {
        for (const declaration of node.declarationList.declarations) {
          if (ts.isIdentifier(declaration.name)) {
            const symbol = this.checker.getSymbolAtLocation(declaration.name)
            if (symbol) {
              componentsInfo.push(
                ...this.applyExportType(this.parser.parseExportSymbol(symbol, scannedSource), exportType),
              )
            }
          }
        }

        return
      }

      // Handle default exports (export default X)
      if (ts.isExportAssignment(node)) {
        const symbol = this.checker.getSymbolAtLocation(node.expression)
        if (symbol) {
          componentsInfo.push(...this.applyExportType(this.parser.parseExportSymbol(symbol, scannedSource), exportType))
        }

        return
      }

      // Handle re-exports (export * from 'module')
      if (ts.isExportDeclaration(node) && node.moduleSpecifier && ts.isStringLiteral(node.moduleSpecifier)) {
        const modulePath = node.moduleSpecifier.text

        // star export = export * from 'module'
        const isStarExport = !node.exportClause

        if (!isStarExport) {
          return
        }

        const resolvedPath = resolveModulePath(scannedSource.fileName, modulePath)
        if (!resolvedPath || !fs.existsSync(resolvedPath)) {
          // ignore star export if module path does not exist
          return
        }

        const newScannedSource = this.program.getSourceFile(resolvedPath)
        if (newScannedSource) {
          const newComponents = this.parseFile(newScannedSource, "star-export")
          componentsInfo.push(...newComponents)
        }
      }
    })

    return componentsInfo
  }
}

// Helper function to check if node has export modifier
function hasExportModifier(node: ts.Node): boolean {
  return (ts.canHaveModifiers(node) && node.modifiers?.some(mod => mod.kind === ts.SyntaxKind.ExportKeyword)) || false
}
